import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { BalanceCard } from '../components/balance/BalanceCard';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import {
  useGetBalanceQuery,
  useClaimReferralMutation,
  useClaimCashbackMutation,
} from '../store/api/balanceApi';
import { Loader } from '@/components/ui/Loader';

export function BalancePage() {
  const { t } = useTranslation();
  const { data, isLoading, error, refetch } = useGetBalanceQuery();
  
  // Claim mutations
  const [claimReferral, { isLoading: isClaimingReferral }] = useClaimReferralMutation();
  const [claimCashback, { isLoading: isClaimingCashback }] = useClaimCashbackMutation();
  
  // Dialog states
  const [showReferralDialog, setShowReferralDialog] = useState(false);
  const [showCashbackDialog, setShowCashbackDialog] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-center items-center py-12">
              <Loader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-4xl mx-auto">
            <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
              <h2 className="text-2xl font-heading font-bold text-danger mb-4">
                {t('balance.error.title')}
              </h2>
              <p className="text-neutral-medium font-body mb-6">
                {'data' in (error as { data?: { message?: string } })
                  ? ((error as { data?: { message?: string } })?.data?.message || t('errors.network'))
                  : t('errors.network')}
              </p>
              <Link
                to="/profile"
                className="inline-block px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
              >
                {t('balance.error.back')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { balance, referralBalance, cashbackBalance } = data.data;

  const handleClaimReferral = async () => {
    try {
      await claimReferral().unwrap();
      toast.success(t('balance.claim.referral.success'));
      setShowReferralDialog(false);
      void refetch();
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('balance.claim.referral.error')
      );
    }
  };

  const handleClaimCashback = async () => {
    try {
      await claimCashback().unwrap();
      toast.success(t('balance.claim.cashback.success'));
      setShowCashbackDialog(false);
      void refetch();
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('balance.claim.cashback.error')
      );
    }
  };

  return (
    <div className="min-h-screen relative">
      <div className="container mx-auto px-4 py-16 content-overlay">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8 text-center animate-slide-up">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-4">
              {t('balance.title')}
            </h1>
            <p className="text-neutral-medium text-lg font-body">
              {t('balance.subtitle')}
            </p>
          </div>

          {/* Balance Cards – داخل كرت واحد */}
          <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8 mb-8 animate-scale-in">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <BalanceCard
                title={t('balance.mainBalance')}
                amount={balance}
                currency="USD"
              />
              {/* Referral Balance with Claim Button */}
              <div className="bg-neutral-dark/30 rounded-lg p-4 md:p-6 transition-all duration-300 hover:bg-neutral-dark/40 hover:scale-105">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-neutral-medium font-body">
                    {t('balance.referralBalance')}
                  </label>
                </div>
                <p className="text-2xl md:text-3xl font-bold text-brand-accent font-heading mb-3">
                  {referralBalance.toFixed(2)} USD
                </p>
                {referralBalance > 0 && (
                  <button
                    onClick={() => setShowReferralDialog(true)}
                    className="w-full px-4 py-2 bg-green-500/20 text-green-400 font-heading font-semibold rounded-lg hover:bg-green-500/30 border border-green-500/30 transition-all duration-300 text-sm"
                  >
                    {t('balance.claim.referral.button')}
                  </button>
                )}
              </div>
              {/* Cashback Balance with Claim Button */}
              <div className="bg-neutral-dark/30 rounded-lg p-4 md:p-6 transition-all duration-300 hover:bg-neutral-dark/40 hover:scale-105">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-neutral-medium font-body">
                    {t('balance.cashbackBalance')}
                  </label>
                </div>
                <p className="text-2xl md:text-3xl font-bold text-brand-accent font-heading mb-3">
                  {cashbackBalance.toFixed(2)} USD
                </p>
                {cashbackBalance > 0 && (
                  <button
                    onClick={() => setShowCashbackDialog(true)}
                    className="w-full px-4 py-2 bg-yellow-500/20 text-yellow-400 font-heading font-semibold rounded-lg hover:bg-yellow-500/30 border border-yellow-500/30 transition-all duration-300 text-sm"
                  >
                    {t('balance.claim.cashback.button')}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8 animate-scale-in">
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/recharge"
                className="flex-1 px-6 py-4 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 text-center"
              >
                {t('balance.actions.recharge')}
              </Link>
              <Link
                to="/transactions"
                className="flex-1 px-6 py-4 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transform hover:scale-105 transition-all duration-300 text-center"
              >
                {t('balance.actions.viewTransactions')}
              </Link>
              <Link
                to="/profile"
                className="flex-1 px-6 py-4 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transform hover:scale-105 transition-all duration-300 text-center"
              >
                {t('balance.actions.backToProfile')}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Claim Referral Dialog */}
      <ConfirmDialog
        isOpen={showReferralDialog}
        onClose={() => setShowReferralDialog(false)}
        onConfirm={handleClaimReferral}
        title={t('balance.claim.referral.title')}
        message={t('balance.claim.referral.message', { amount: referralBalance.toFixed(2) })}
        confirmText={t('balance.claim.referral.confirm')}
        cancelText={t('common.cancel')}
        type="info"
        isLoading={isClaimingReferral}
      />

      {/* Claim Cashback Dialog */}
      <ConfirmDialog
        isOpen={showCashbackDialog}
        onClose={() => setShowCashbackDialog(false)}
        onConfirm={handleClaimCashback}
        title={t('balance.claim.cashback.title')}
        message={t('balance.claim.cashback.message', { amount: cashbackBalance.toFixed(2) })}
        confirmText={t('balance.claim.cashback.confirm')}
        cancelText={t('common.cancel')}
        type="info"
        isLoading={isClaimingCashback}
      />
    </div>
  );
}

